<template>
    <section class="workflow-review-overview">
        <b-alert :variant="banner.variant" show class="workflow-review-overview__banner">
            <div class="alert-body d-flex align-items-center">
                <feather-icon :icon="banner.icon" class="mr-75" />
                <strong>{{ banner.label }}</strong>
            </div>
        </b-alert>

        <div class="workflow-review-overview__rows">
            <article
                v-for="contribution in contributions"
                :key="contribution.stepId"
                class="workflow-review-overview__row"
            >
                <header
                    class="workflow-review-overview__row-header"
                    role="button"
                    tabindex="0"
                    :aria-controls="collapseId(contribution)"
                    :aria-expanded="isExpanded(contribution) ? 'true' : 'false'"
                    @click="toggle(contribution)"
                    @keydown.enter.self.prevent="toggle(contribution)"
                    @keydown.space.self.prevent="toggle(contribution)"
                >
                    <span class="workflow-review-overview__step-icon" aria-hidden="true">
                        <feather-icon :icon="contribution.icon" size="20" />
                    </span>
                    <div class="workflow-review-overview__identity">
                        <div class="workflow-review-overview__title-line">
                            <h5 class="mb-0">{{ text(contribution.title) }}</h5>
                            <b-badge :variant="statusMeta(contribution.status).badgeVariant">
                                {{ contributionStatusLabel(contribution) }}
                            </b-badge>
                        </div>
                        <div v-if="contribution.summary" class="workflow-review-overview__summary">
                            {{ text(contribution.summary) }}
                        </div>
                    </div>
                    <b-button
                        variant="outline-secondary"
                        @click.stop="$emit('edit', contribution.stepId)"
                    >
                        {{ translate('Component(Button):edit', 'Edit') }}
                    </b-button>
                    <b-button
                        class="workflow-review-overview__toggle btn-icon"
                        variant="flat-secondary"
                        :aria-controls="collapseId(contribution)"
                        :aria-expanded="isExpanded(contribution) ? 'true' : 'false'"
                        :aria-label="toggleLabel(contribution)"
                        @click.stop="toggle(contribution)"
                    >
                        <feather-icon
                            :icon="isExpanded(contribution) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                        />
                    </b-button>
                </header>

                <b-collapse
                    :id="collapseId(contribution)"
                    :visible="isExpanded(contribution)"
                >
                    <div class="workflow-review-overview__details">
                        <dl
                            v-if="contribution.facts.length > 0"
                            class="workflow-review-overview__facts"
                        >
                            <div
                                v-for="fact in contribution.facts"
                                :key="fact.id"
                                class="workflow-review-overview__fact"
                            >
                                <dt>{{ text(fact.label) }}</dt>
                                <dd>{{ text(fact.value) }}</dd>
                            </div>
                        </dl>

                        <div
                            v-if="contribution.checks.length > 0"
                            class="workflow-review-overview__check-groups"
                        >
                            <section
                                v-for="group in checkGroups(contribution)"
                                :key="group.id"
                                class="workflow-review-overview__check-group"
                            >
                                <div
                                    v-if="group.title"
                                    class="workflow-review-overview__check-group-heading"
                                >
                                    <strong>{{ text(group.title) }}</strong>
                                    <span v-if="group.subtitle" class="text-less-muted">
                                        {{ text(group.subtitle) }}
                                    </span>
                                </div>
                                <ul class="workflow-review-overview__checks">
                                    <li v-for="check in group.checks" :key="check.id">
                                        <feather-icon
                                            :icon="check.status === 'pending'
                                                ? 'ClockIcon'
                                                : 'AlertCircleIcon'"
                                            class="mr-50"
                                        />
                                        {{ text(check.message) }}
                                    </li>
                                </ul>
                            </section>
                        </div>
                    </div>
                </b-collapse>
            </article>
        </div>
    </section>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCollapse,
} from 'bootstrap-vue'

const STATUS_PRIORITY = Object.freeze({
    complete: 0,
    pending: 1,
    attention: 2,
    blocked: 3,
})

export default {
    name: 'WorkflowReviewOverview',
    components: {
        BAlert,
        BBadge,
        BButton,
        BCollapse,
    },
    props: {
        contributions: { type: Array, required: true },
    },
    data() {
        return {
            expandedByStepId: {},
        }
    },
    computed: {
        aggregateStatus() {
            return this.contributions.reduce((current, contribution) => (
                STATUS_PRIORITY[contribution.status] > STATUS_PRIORITY[current]
                    ? contribution.status
                    : current
            ), 'complete')
        },
        banner() {
            const banners = {
                complete: {
                    variant: 'success',
                    icon: 'CheckCircleIcon',
                    label: this.translate(
                        'Page(ReceivedRequest):Component(WorkflowReview):all-complete',
                        'All required information is complete',
                    ),
                },
                attention: {
                    variant: 'warning',
                    icon: 'AlertCircleIcon',
                    label: this.translate(
                        'Page(ReceivedRequest):Component(WorkflowReview):needs-attention',
                        'Some information may need your attention',
                    ),
                },
                blocked: {
                    variant: 'danger',
                    icon: 'XCircleIcon',
                    label: this.translate(
                        'Page(ReceivedRequest):Component(WorkflowReview):blocked',
                        'Complete the required information before submitting',
                    ),
                },
                pending: {
                    variant: 'secondary',
                    icon: 'ClockIcon',
                    label: this.translate(
                        'Page(ReceivedRequest):Component(WorkflowReview):pending',
                        'Review information is loading',
                    ),
                },
            }

            return banners[this.aggregateStatus]
        },
    },
    watch: {
        contributions: {
            immediate: true,
            handler(contributions) {
                contributions.forEach(contribution => {
                    if (contribution.status === 'blocked') {
                        this.$set(this.expandedByStepId, contribution.stepId, true)
                    } else if (!Object.prototype.hasOwnProperty.call(
                        this.expandedByStepId,
                        contribution.stepId,
                    )) {
                        this.$set(
                            this.expandedByStepId,
                            contribution.stepId,
                            contribution.initiallyExpanded === true,
                        )
                    }
                })
            },
        },
    },
    methods: {
        translate(key, fallback, params = {}) {
            const translation = key ? this.$t(key, params) : null
            return translation && translation !== key ? translation : fallback
        },
        text(value) {
            if (value === null || typeof value === 'undefined') {
                return '—'
            }

            if (typeof value !== 'object') {
                return String(value)
            }

            return this.translate(value.key, value.fallback, value.params)
        },
        statusMeta(status) {
            const statuses = {
                complete: {
                    badgeVariant: 'light-success',
                    label: this.translate('Component(Status):complete', 'Complete'),
                },
                attention: {
                    badgeVariant: 'light-warning',
                    label: this.translate(
                        'Component(Status):information-required',
                        'Needs attention',
                    ),
                },
                blocked: {
                    badgeVariant: 'light-danger',
                    label: this.translate('Component(Status):incomplete', 'Incomplete'),
                },
                pending: {
                    badgeVariant: 'light-secondary',
                    label: this.translate('Component(Status):loading', 'Loading'),
                },
            }

            return statuses[status] || statuses.attention
        },
        contributionStatusLabel(contribution) {
            return contribution.statusLabel
                ? this.text(contribution.statusLabel)
                : this.statusMeta(contribution.status).label
        },
        checkGroups(contribution) {
            const groups = []
            const groupsById = {}

            contribution.checks.forEach(check => {
                const metadata = check.group || null
                const id = metadata ? `group-${metadata.id}` : 'ungrouped'
                if (!groupsById[id]) {
                    groupsById[id] = {
                        id,
                        title: metadata && metadata.title,
                        subtitle: metadata && metadata.subtitle,
                        checks: [],
                    }
                    groups.push(groupsById[id])
                }
                groupsById[id].checks.push(check)
            })

            return groups
        },
        collapseId(contribution) {
            return `workflow-review-${contribution.stepId}`
        },
        isExpanded(contribution) {
            return this.expandedByStepId[contribution.stepId] === true
        },
        toggle(contribution) {
            this.$set(
                this.expandedByStepId,
                contribution.stepId,
                !this.isExpanded(contribution),
            )
        },
        toggleLabel(contribution) {
            return this.isExpanded(contribution)
                ? this.translate('Component(Button):collapse', 'Collapse')
                : this.translate('Component(Button):expand', 'Expand')
        },
    },
}
</script>

<style lang="scss" scoped>
.workflow-review-overview {
    margin-bottom: 1.5rem;

    &__banner {
        margin-bottom: 1.25rem;
    }

    &__rows {
        display: grid;
        gap: .75rem;
    }

    &__row {
        border: 1px solid #ebe9f1;
        border-radius: .357rem;
        background: #fff;
    }

    &__row-header {
        align-items: center;
        cursor: pointer;
        display: flex;
        gap: 1rem;
        min-height: 4.25rem;
        padding: .75rem 1rem;
        transition: background-color .15s ease;

        &:hover {
            background-color: rgba(115, 103, 240, .025);
        }

        &:focus-visible {
            outline: 2px solid rgba(115, 103, 240, .25);
            outline-offset: -2px;
        }
    }

    &__step-icon {
        align-items: center;
        color: #5e5873;
        display: inline-flex;
        flex: 0 0 2rem;
        justify-content: center;
    }

    &__identity {
        min-width: 0;
        flex: 1 1 auto;
    }

    &__title-line {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: .75rem;
    }

    &__summary {
        color: #5e5873;
        font-size: .875rem;
        margin-top: .25rem;
    }

    &__details {
        border-top: 1px solid #ebe9f1;
        padding: 1rem 1rem 1rem 4rem;
    }

    &__facts {
        display: grid;
        gap: .75rem 1.5rem;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        margin: 0;
    }

    &__fact {
        dt {
            color: #6e6b7b;
            font-size: .8rem;
            font-weight: 600;
        }

        dd {
            margin: .2rem 0 0;
        }
    }

    &__check-groups {
        margin: 1rem 0 0;
    }

    &__check-group + &__check-group {
        margin-top: 1rem;
    }

    &__check-group-heading {
        align-items: baseline;
        color: #4a455b;
        display: flex;
        flex-wrap: wrap;
        gap: .5rem;
        margin-bottom: .4rem;

        strong {
            font-weight: 600;
        }
    }

    &__checks {
        color: #b76e00;
        list-style: none;
        margin: 0;
        padding: 0;

        li + li {
            margin-top: .5rem;
        }
    }
}

@media (max-width: 767.98px) {
    .workflow-review-overview {
        &__row-header {
            align-items: flex-start;
            flex-wrap: wrap;
        }

        &__identity {
            flex-basis: calc(100% - 4rem);
        }

        &__details {
            padding-left: 1rem;
        }

        &__facts {
            grid-template-columns: 1fr;
        }
    }
}
</style>
